/**
 * Modify Campaign hook.
 *
 * Orchestrates form validation, data building, and API submission for
 * campaign modification. Mirrors `useCreateCampaign` so the page/form can
 * reuse the same touched/submitted error-visibility patterns. The modify
 * flow additionally exposes a version-conflict path (HTTP 409) with a
 * `reloadCampaign` helper that re-fetches the latest server state.
 */

import { useState, useCallback, useRef } from 'react';
import { CampaignType, OperationStatus } from 'src/@types/valueMatching/enums';
import { CampaignConfigurationData } from 'src/@types/valueMatching/campaignConfiguration';
import { CampaignASIN } from 'src/@types/valueMatching/common';
import { CampaignData } from 'src/@types/valueMatching/campaign';
import { UseCampaignFormResult } from 'src/hooks/useCampaignForm/useCampaignForm';
import { modifyCampaign } from 'src/accessors/valueMatchingAccessor';
import { buildCampaignDataFromForm } from 'src/utils/valueMatching/form/formDataBuilder';
import {
    validateCampaignForm,
    FieldErrors,
    FieldKey,
} from 'src/utils/valueMatching/form/formValidationUtils';
import { logError } from 'src/utils/monitoring';
import { useTranslate } from 'src/hooks/useTranslate';
import { useCampaignFormValidation } from 'src/hooks/useCampaignFormValidation';
import { ERROR_KEYS } from 'src/constants';

const DEFAULT_PROPAGATION_DELAY_MINUTES = 0;
const MODIFY_CAMPAIGN_BUNDLE = 'pages.modifyCampaign';

/**
 * Regex to detect HTTP 409 version conflicts in error messages from `monitoredFetch`.
 * Matches variations like "status: 409", "status:409", "statusCode: 409", "HTTP 409", "409 Conflict".
 * monitoredFetch currently throws: `status: ${status} | statusText: ${text}` (see helpers.ts).
 */
const VERSION_CONFLICT_PATTERN = /\b(?:status(?:Code)?:\s*409|HTTP\s+409|409\s+Conflict)\b/i;

export interface UseModifyCampaignParams {
    /** The current form state from `useCampaignForm`. */
    form: UseCampaignFormResult;
    /**
     * Server-provided campaign configuration fetched via `useCampaignConfiguration`.
     * May be `null` while the config call is in-flight or has failed.
     */
    config: CampaignConfigurationData | null;
    /**
     * The campaign type parsed from the URL route param. `undefined` when the
     * slug is missing/unrecognised or the route is still resolving.
     */
    campaignType: CampaignType | undefined;
    /**
     * Existing campaign data loaded via `useCampaignDetails`. Passed straight
     * through to `buildCampaignDataFromForm` so the submitted payload carries
     * the existing `campaignId` and `version` (used by the backend to detect
     * concurrent edits).
     *
     * `null` while the details fetch is in-flight or has failed. The page
     * gates the form behind a non-null check, so by the time the user can
     * click submit this should be set; `submitModification` short-circuits
     * defensively if it is still `null`.
     */
    existingData: CampaignData | null;
    /**
     * Callback to re-fetch campaign details. Injected from the page so the hook
     * does not need a direct handle to `useCampaignDetails`. Called when the user
     * clicks "Reload Latest Version" on a version-conflict Alert.
     */
    refetchDetails: () => void;
}

export interface UseModifyCampaignResult {
    /** Triggers form validation → data build → API submission */
    submitModification: () => Promise<void>;
    /** Marks a field as touched so its error is shown from now on. */
    markTouched: (field: FieldKey) => void;
    /** True while the modifyCampaign API call is in-flight. Used to show a loading spinner and disable the submit button. Always `false` initially and after the call completes. */
    submitting: boolean;
    /** Non-field (submission/API) error message; `null` when there is no error (never `undefined`). */
    error: string | null;
    /**
     * Per-field error map for inline `errorText`. Visible only for touched
     * fields, unless the user has submitted at least once.
     * Always an object (never `null` or `undefined`); empty `{}` means no visible errors.
     */
    fieldErrors: FieldErrors;
    /** `true` after a successful campaign modification; `false` initially and on failure. */
    success: boolean;
    /** ASINs that conflict with existing campaigns. Empty array `[]` when there are no conflicts (never `null` or `undefined`). */
    conflictingCampaignASINs: CampaignASIN[];
    /** `true` when the backend reported a version conflict (HTTP 409). Used by the page to render the dedicated reload Alert in place of the generic error. */
    versionConflict: boolean;
    /** Clears the version-conflict state and re-fetches the latest campaign details. */
    reloadCampaign: () => void;
}

/**
 * Hook that manages the modify-campaign submission lifecycle.
 *
 * Validation visibility rules:
 * - On load: no errors shown.
 * - Once a field is touched: its error becomes visible and updates live.
 * - On submit: all errors become visible regardless of touched state.
 *
 * @param params - See {@link UseModifyCampaignParams}. `form` is always defined
 *   (the form hook initialises all fields to safe defaults). `config` may be
 *   `null` while the config fetch is in-flight — in that case the propagation
 *   delay falls back to 0 (no start-time guard). `campaignType` may be `undefined` when
 *   the URL slug is missing/unrecognised; when `undefined`, `fieldErrors`
 *   returns `{}` and `submitModification` short-circuits with a user-facing
 *   error.
 */
export function useModifyCampaign(params: UseModifyCampaignParams): UseModifyCampaignResult {
    const { form, config, campaignType, existingData, refetchDetails } = params;

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [conflictingCampaignASINs, setConflictingCampaignASINs] = useState<CampaignASIN[]>([]);
    const [versionConflict, setVersionConflict] = useState(false);
    const { translate } = useTranslate(MODIFY_CAMPAIGN_BUNDLE);

    const { fieldErrors, markTouched, markSubmitted } = useCampaignFormValidation({
        form,
        config,
        campaignType,
        formType: 'modify',
    });

    // ── Concurrency guard ───────────────────────────────────────────────
    // `inFlightRef` prevents duplicate submissions when the user double-clicks
    // the submit button. A ref (not state) is used because we don't want a
    // re-render when flipping the flag — we just need a mutable boolean that
    // persists across renders.
    const inFlightRef = useRef(false);

    /**
     * Submit flow — called when the user clicks "Save Changes".
     *
     * High-level flow:
     *   1. Guard against concurrent submissions (inFlightRef).
     *   2. Reset all state from any previous submission attempt.
     *   3. Bail out early if campaignType is not yet available.
     *   4. Run client-side validation; bail out if invalid (fieldErrors
     *      become visible via the `submitted` flag).
     *   5. Build the API payload from form state (including existing
     *      campaignId + version from `existingData`).
     *   6. POST to the BFF modify-campaign endpoint.
     *   7. On SUCCESS → set success=true.
     *      On FAILURE with conflicts → surface conflictingCampaignASINs.
     *      On FAILURE without conflicts → show generic error message.
     *   8. Surface any conflicting ASINs returned by the API.
     *   9. On 409 / version conflict → show dedicated reload Alert.
     *      On other network/unexpected error → log + show generic error.
     *  10. Always clear submitting spinner and release concurrency guard.
     */
    const submitModification = useCallback(async () => {
        // ── Step 1: Concurrency guard ───────────────────────────────────
        // If a submission is already in-flight, silently ignore the call.
        // This prevents duplicate API calls from rapid double-clicks.
        if (inFlightRef.current) return;
        inFlightRef.current = true;

        try {
            // ── Step 2: Reset all UI state ──────────────────────────────────
            // Clear stale results from any prior submission (success, error,
            // conflicts, version-conflict flag) so the user sees a clean
            // slate. We do this at the very top — before any early returns —
            // to guarantee old data never lingers in the UI.
            markSubmitted();   // Flip the "has-submitted" flag so ALL field errors become visible.
            setSuccess(false);
            setConflictingCampaignASINs([]);
            setError(null);
            setVersionConflict(false);

            // ── Step 3: Guard — campaign type and existingData must be set ──
            // campaignType is undefined when the URL param is invalid or the
            // config hasn't loaded yet. existingData is null while the details
            // fetch is in-flight. The page's loading gate prevents the user
            // from clicking submit in either case; this guard is defensive
            // against race conditions or future refactors.
            if (!campaignType || !existingData) {
                setError(translate(ERROR_KEYS.CAMPAIGN_TYPE_UNAVAILABLE));
                return;
            }

            // ── Step 4: Client-side validation ──────────────────────────────
            // Run all field validators (title, ASINs, dates, type-specific
            // rules, plus modify-only suspended-ASIN subset check). If any
            // fail, `validation.isValid` is false. The `markSubmitted()` call
            // above causes the shared validation hook to surface ALL field
            // errors regardless of touched state. We do NOT call the API.
            const propagationDelay = config?.propagationDelayInMinutes ?? DEFAULT_PROPAGATION_DELAY_MINUTES;
            const validation = validateCampaignForm(form, campaignType, 'modify', propagationDelay);

            if (!validation.isValid) return;

            // ── Step 5–9: API call ──────────────────────────────────────────
            // Validation passed → show the loading spinner and make the call.
            setSubmitting(true);

            try {
                // Step 5: Build the API request payload by transforming
                //   the form state into the CampaignData shape the BFF
                //   expects. Passing `existingData` through ensures
                //   `campaignId` and `version` are carried in the payload
                //   so the backend can detect concurrent edits.
                const campaignData = buildCampaignDataFromForm(form, campaignType, config, existingData);

                // Step 6: POST to the BFF modify-campaign endpoint.
                //   The BFF expects `campaignId` at the top level (per Smithy model)
                //   alongside `campaignData`. The accessor handles CSRF tokens, fetch,
                //   and JSON parsing.
                const campaignId = existingData.campaignId ?? '';
                const response = await modifyCampaign({ campaignId, campaignData });

                // Steps 7–8: Process the API response.
                //   The BFF returns an `operationStatus` enum:
                //   - SUCCESS → campaign was modified. The page uses the
                //     `success` flag to navigate to the list page.
                //   - FAILURE with conflictingCampaignASINs → populate
                //     the array so the page can render a ConflictTable.
                //     We do NOT overlay a generic error on this path —
                //     the conflict UI is specific enough on its own.
                //   - FAILURE without conflicts → show a generic error
                //     message. We don't expose raw backend errors to the user.
                if (response.operationStatus === OperationStatus.SUCCESS) {
                    setSuccess(true);
                } else if (response.conflictingCampaignASINs?.length) {
                    setConflictingCampaignASINs(response.conflictingCampaignASINs);
                } else {
                    setError(translate(ERROR_KEYS.GENERIC_SUBMISSION));
                }
            } catch (err) {
                // ── Step 9: Handle network / unexpected errors ──────────────
                // Any exception from fetch (network timeout, DNS failure,
                // CSRF missing, JSON parse error, HTTP 409 version conflict,
                // etc.) lands here. `monitoredFetch` currently wraps HTTP
                // failures in a message string that includes `status: 409`
                // for version conflicts; we substring-match to detect them.
                // If matched → dedicated version-conflict flag + translated
                // message (the page renders a Reload alert). Otherwise → log
                // the raw message for internal observability and show a
                // generic user-facing message to avoid leaking internal
                // details (URLs, stack traces, etc.).
                const message = err instanceof Error ? err.message : String(err);
                if (VERSION_CONFLICT_PATTERN.test(message)) {
                    setVersionConflict(true);
                    setError(translate(ERROR_KEYS.VERSION_CONFLICT));
                } else {
                    logError(message, { methodName: 'useModifyCampaign' });
                    setError(translate(ERROR_KEYS.GENERIC_SUBMISSION));
                }
            } finally {
                // ── Step 10: Always hide the spinner ────────────────────────
                // Whether the call succeeded, failed, or threw — stop
                // showing the loading indicator and re-enable the button.
                setSubmitting(false);
            }
        } finally {
            // Release the concurrency guard so subsequent clicks are
            // allowed (e.g., user fixes errors and resubmits).
            inFlightRef.current = false;
        }
    }, [form, campaignType, config, existingData, translate, markSubmitted]);

    /**
     * Reload flow — called when the user clicks "Reload Latest Version" on
     * the version-conflict Alert. Clears the conflict flag and the error
     * message so the Alert disappears, then delegates to the injected
     * `refetchDetails` callback (which owns the actual re-fetch).
     */
    const reloadCampaign = useCallback(() => {
        setVersionConflict(false);
        setError(null);
        refetchDetails();
    }, [refetchDetails]);

    return {
        submitModification,
        markTouched,
        submitting,
        error,
        fieldErrors,
        success,
        conflictingCampaignASINs,
        versionConflict,
        reloadCampaign,
    };
}
